// ============================================
// src/components/expenses/ExpenseManagement.js
// Complete Expense Management Component
// ============================================

// src/components/expenses/ExpenseManagement.js
import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase.config';
import '../../pages/UserDashboard.css';
import './ExpenseManagement.css';

function ExpenseManagement({ userData, onBack }) {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [activeTab, setActiveTab] = useState('list');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  
  const [filters, setFilters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    category: 'all',
    status: 'all'
  });
  
  const [stats, setStats] = useState({
    totalExpenses: 0,
    approvedExpenses: 0,
    pendingExpenses: 0,
    monthlyTotal: 0
  });

  const isAdmin = userData?.role === 'admin' || userData?.role === 'superadmin';
  const isSuperAdmin = userData?.role === 'superadmin';

  useEffect(() => {
    fetchCategories();
    fetchVendors();
    fetchExpenses();
    if (darkMode) {
      document.body.classList.add('dark-mode');
    }
  }, [filters, darkMode]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('expense_categories')
        .select('*')
        .eq('is_active', true)
        .order('name');
      
      if (!error) setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchVendors = async () => {
    try {
      const { data, error } = await supabase
        .from('service_vendors')
        .select('id, name')
        .eq('is_active', true)
        .order('name');
      
      if (!error) setVendors(data || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('expenses')
        .select('*')
        .eq('fiscal_year', filters.year)
        .eq('fiscal_month', filters.month)
        .order('expense_date', { ascending: false });

      if (filters.category !== 'all') {
        query = query.eq('category_id', filters.category);
      }
      if (filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      const { data, error } = await query;
      
      if (!error) {
        // Fetch related data separately if needed
        const expensesWithDetails = await Promise.all((data || []).map(async (expense) => {
          const [category, vendor, creator] = await Promise.all([
            supabase.from('expense_categories').select('name, icon').eq('id', expense.category_id).single(),
            expense.vendor_id ? supabase.from('service_vendors').select('name').eq('id', expense.vendor_id).single() : null,
            supabase.from('residents').select('name').eq('id', expense.created_by).single()
          ]);
          
          return {
            ...expense,
            expense_categories: category.data,
            service_vendors: vendor?.data,
            residents: creator.data
          };
        }));
        
        setExpenses(expensesWithDetails);
        calculateStats(expensesWithDetails);
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (expenseData) => {
    const total = expenseData.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
    const approved = expenseData.filter(e => e.status === 'approved' || e.status === 'paid')
      .reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
    const pending = expenseData.filter(e => e.status === 'pending').length;
    
    setStats({
      totalExpenses: expenseData.length,
      approvedExpenses: approved,
      pendingExpenses: pending,
      monthlyTotal: total
    });
  };

  const handleAddExpense = async (expenseData) => {
    try {
      // Validation
      const expenseDate = new Date(expenseData.expense_date);
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      
      if (expenseDate > today) {
        alert('Cannot add expenses for future dates');
        return;
      }

      if (parseFloat(expenseData.amount) <= 0) {
        alert('Amount must be greater than 0');
        return;
      }

      // Check for duplicate
      const { data: existing } = await supabase
        .from('expenses')
        .select('id')
        .eq('title', expenseData.title)
        .eq('amount', expenseData.amount)
        .eq('expense_date', expenseData.expense_date)
        .single();

      if (existing) {
        if (!window.confirm('An expense with similar details already exists. Continue?')) {
          return;
        }
      }

      const insertData = {
        category_id: expenseData.category_id,
        title: expenseData.title.trim(),
        description: expenseData.description?.trim() || null,
        amount: parseFloat(expenseData.amount),
        expense_date: expenseData.expense_date,
        payment_method: expenseData.payment_method || 'cash',
        notes: expenseData.notes?.trim() || null,
        vendor_id: expenseData.vendor_id || null,
        created_by: userData.id,
        fiscal_year: expenseDate.getFullYear(),
        fiscal_month: expenseDate.getMonth() + 1,
        status: isAdmin ? 'approved' : 'pending',
        created_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('expenses')
        .insert([insertData])
        .select();

      if (error) {
        console.error('Error adding expense:', error);
        alert('Failed to add expense: ' + error.message);
      } else {
        setShowAddModal(false);
        fetchExpenses();
        alert('Expense added successfully!');
      }
    } catch (error) {
      console.error('Error adding expense:', error);
      alert('Failed to add expense');
    }
  };

  const handleEditExpense = async (expenseData) => {
    try {
      const updateData = {
        category_id: expenseData.category_id,
        title: expenseData.title.trim(),
        description: expenseData.description?.trim() || null,
        amount: parseFloat(expenseData.amount),
        expense_date: expenseData.expense_date,
        payment_method: expenseData.payment_method || 'cash',
        notes: expenseData.notes?.trim() || null,
        vendor_id: expenseData.vendor_id || null,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('expenses')
        .update(updateData)
        .eq('id', editingExpense.id);

      if (!error) {
        setShowEditModal(false);
        setEditingExpense(null);
        fetchExpenses();
        alert('Expense updated successfully!');
      } else {
        alert('Failed to update expense: ' + error.message);
      }
    } catch (error) {
      console.error('Error updating expense:', error);
      alert('Failed to update expense');
    }
  };

  const handleDeleteExpense = async (expense) => {
    if (!isSuperAdmin) {
      alert('Only superadmin can delete expenses');
      return;
    }

    if (window.confirm(`Delete expense: ${expense.title}?`)) {
      try {
        const { error } = await supabase
          .from('expenses')
          .delete()
          .eq('id', expense.id);

        if (!error) {
          fetchExpenses();
          alert('Expense deleted successfully!');
        }
      } catch (error) {
        console.error('Error deleting expense:', error);
        alert('Failed to delete expense');
      }
    }
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    document.body.classList.toggle('dark-mode');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const exportToExcel = () => {
    let csvContent = "Date,Category,Title,Amount,Vendor,Status,Created By\n";
    
    expenses.forEach(expense => {
      csvContent += `${expense.expense_date},${expense.expense_categories?.name},${expense.title},${expense.amount},${expense.service_vendors?.name || '-'},${expense.status},${expense.residents?.name}\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Expenses_${filters.month}_${filters.year}.csv`;
    a.click();
  };

  return (
    <div className={`dashboard-container expense-management-container ${darkMode ? 'dark-mode' : ''}`}>
      <div 
        className={`mobile-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={closeMobileMenu}
      ></div>

      {/* Sidebar Navigation */}
      <aside className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="user-info">
            <div className="user-avatar">
              {userData?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <h3>{userData?.name}</h3>
              <p>Flat {userData?.flat_number}</p>
            </div>
          </div>
        </div>
        
        <nav className="sidebar-menu">
          <button
            className="menu-item"
            onClick={onBack}
          >
            <span className="menu-icon">🏠</span>
            <span className="menu-label">Dashboard</span>
          </button>
          
          <div className="sidebar-divider"></div>
          
          <button
            className={`menu-item ${activeTab === 'list' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('list');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">📋</span>
            <span className="menu-label">List View</span>
          </button>
          
          <button
            className={`menu-item ${activeTab === 'summary' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('summary');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">📊</span>
            <span className="menu-label">Summary</span>
          </button>
          
          {isAdmin && (
            <button
              className="menu-item"
              onClick={() => {
                setShowAddModal(true);
                closeMobileMenu();
              }}
            >
              <span className="menu-icon">➕</span>
              <span className="menu-label">Add Expense</span>
            </button>
          )}
          
          <div className="sidebar-divider"></div>
          
          <div className="filter-section">
            <label className="filter-label">Filters</label>
            
            <select
              value={filters.month}
              onChange={(e) => setFilters({...filters, month: parseInt(e.target.value)})}
              className="filter-select"
            >
              {[...Array(12)].map((_, i) => (
                <option key={i+1} value={i+1}>
                  {new Date(2024, i).toLocaleDateString('en-US', { month: 'long' })}
                </option>
              ))}
            </select>
            
            <select
              value={filters.year}
              onChange={(e) => setFilters({...filters, year: parseInt(e.target.value)})}
              className="filter-select"
            >
              {[2024, 2025, 2026].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            
            <select
              value={filters.category}
              onChange={(e) => setFilters({...filters, category: e.target.value})}
              className="filter-select"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.icon} {cat.name}
                </option>
              ))}
            </select>
            
            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="paid">Paid</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </nav>
        
        <div className="sidebar-footer">
          <button className="theme-toggle" onClick={toggleDarkMode}>
            <span className="menu-icon">{darkMode ? '☀️' : '🌙'}</span>
            <span className="menu-label">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="dashboard-main">
        {/* Mobile Header */}
        <header className="dashboard-header enhanced">
          <div className="header-left">
            <button 
              className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            <h2 className="page-title-mobile">💰 Expenses</h2>
          </div>
        </header>

        <div className="dashboard-content">
          {/* Desktop Header */}
          <div className="expense-header">
            <h2 className="page-title">💰 Expense Management</h2>
            <div className="header-actions">
              <button onClick={exportToExcel} className="btn-secondary">
                📊 Export
              </button>
              {isAdmin && (
                <button onClick={() => setShowAddModal(true)} className="btn-primary">
                  + Add Expense
                </button>
              )}
            </div>
          </div>

          {/* Stats Cards */}
          <div className="dashboard-grid stats-grid">
            <div className="dashboard-card stat-card gradient-purple">
              <div className="stat-content">
                <h3>📊 Total</h3>
                <p className="stat-value">{stats.totalExpenses}</p>
                <small>Expenses</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-green">
              <div className="stat-content">
                <h3>💵 Monthly</h3>
                <p className="stat-value">₹{stats.monthlyTotal.toFixed(2)}</p>
                <small>Total</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-cyan">
              <div className="stat-content">
                <h3>✅ Approved</h3>
                <p className="stat-value">₹{stats.approvedExpenses.toFixed(2)}</p>
                <small>Amount</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-yellow">
              <div className="stat-content">
                <h3>⏳ Pending</h3>
                <p className="stat-value">{stats.pendingExpenses}</p>
                <small>Items</small>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="dashboard-card">
            {loading ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading expenses...</p>
              </div>
            ) : activeTab === 'list' ? (
              <ExpenseList
                expenses={expenses}
                onEdit={(expense) => {
                  setEditingExpense(expense);
                  setShowEditModal(true);
                }}
                onDelete={handleDeleteExpense}
                isAdmin={isAdmin}
                isSuperAdmin={isSuperAdmin}
              />
            ) : (
              <ExpenseSummary
                expenses={expenses}
                categories={categories}
              />
            )}
          </div>
        </div>
      </main>

      {/* Modals */}
      {(showAddModal || showEditModal) && (
        <ExpenseModal
          expense={editingExpense}
          categories={categories}
          vendors={vendors}
          onSave={showEditModal ? handleEditExpense : handleAddExpense}
          onClose={() => {
            setShowAddModal(false);
            setShowEditModal(false);
            setEditingExpense(null);
          }}
        />
      )}
    </div>
  );
}



// Stat Card Component
function StatCard({ title, value, icon, color }) {
  return (
    <div style={{
      background: 'white',
      padding: '20px',
      borderRadius: '10px',
      boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
      borderLeft: `4px solid ${color}`
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>{title}</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color }}>{value}</div>
        </div>
        <div style={{ fontSize: '30px' }}>{icon}</div>
      </div>
    </div>
  );
}

// Expense List Component
function ExpenseList({ expenses, onEdit, onDelete, onApprove, isAdmin, isSuperAdmin }) {
  return (
    <div style={{
      background: 'white',
      borderRadius: '10px',
      overflow: 'hidden',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#f8f9fa' }}>
            <th style={tableHeaderStyle}>Date</th>
            <th style={tableHeaderStyle}>Category</th>
            <th style={tableHeaderStyle}>Title</th>
            <th style={tableHeaderStyle}>Amount</th>
            <th style={tableHeaderStyle}>Vendor</th>
            <th style={tableHeaderStyle}>Status</th>
            <th style={tableHeaderStyle}>Created By</th>
            <th style={tableHeaderStyle}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {expenses.length === 0 ? (
            <tr>
              <td colSpan="8" style={{ padding: '20px', textAlign: 'center' }}>
                No expenses found
              </td>
            </tr>
          ) : (
            expenses.map(expense => (
              <tr key={expense.id}>
                <td style={tableCellStyle}>
                  {new Date(expense.expense_date).toLocaleDateString()}
                </td>
                <td style={tableCellStyle}>
                  {expense.expense_categories?.icon} {expense.expense_categories?.name}
                </td>
                <td style={tableCellStyle}>{expense.title}</td>
                <td style={tableCellStyle}>₹{expense.amount}</td>
                <td style={tableCellStyle}>{expense.service_vendors?.name || '-'}</td>
                <td style={tableCellStyle}>
                  <StatusBadge status={expense.status} />
                </td>
                <td style={tableCellStyle}>{expense.residents?.name}</td>
                <td style={tableCellStyle}>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    {expense.status === 'pending' && isAdmin && (
                      <button
                        onClick={() => onApprove(expense)}
                        style={actionButtonStyle('#28a745')}
                      >
                        ✅
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => onEdit(expense)}
                        style={actionButtonStyle('#ffc107')}
                      >
                        ✏️
                      </button>
                    )}
                    {isSuperAdmin && (
                      <button
                        onClick={() => onDelete(expense)}
                        style={actionButtonStyle('#dc3545')}
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// Expense Summary Component
function ExpenseSummary({ expenses, categories }) {
  const categoryTotals = {};
  
  categories.forEach(cat => {
    const catExpenses = expenses.filter(e => e.category_id === cat.id);
    categoryTotals[cat.name] = {
      icon: cat.icon,
      total: catExpenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0),
      count: catExpenses.length
    };
  });

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
      gap: '20px'
    }}>
      {Object.entries(categoryTotals).map(([category, data]) => (
        <div
          key={category}
          style={{
            background: 'white',
            padding: '20px',
            borderRadius: '10px',
            boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
          }}
        >
          <div style={{ fontSize: '24px', marginBottom: '10px' }}>{data.icon}</div>
          <h4 style={{ margin: '0 0 10px 0' }}>{category}</h4>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#667eea' }}>
            ₹{data.total.toFixed(2)}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {data.count} expense{data.count !== 1 ? 's' : ''}
          </div>
        </div>
      ))}
    </div>
  );
}

// Expense Modal Component
function ExpenseModal({ expense, categories, vendors, onSave, onClose }) {
  const [formData, setFormData] = useState({
    category_id: expense?.category_id || '',
    title: expense?.title || '',
    description: expense?.description || '',
    amount: expense?.amount || '',
    expense_date: expense?.expense_date || new Date().toISOString().split('T')[0],
    vendor_id: expense?.vendor_id || '',  // Keep as empty string for the select
    payment_method: expense?.payment_method || 'cash',
    notes: expense?.notes || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.category_id || !formData.title || !formData.amount || !formData.expense_date) {
      alert('Please fill in all required fields');
      return;
    }
    
    // Pass the form data as is, the handler will clean it
    onSave(formData);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        borderRadius: '10px',
        padding: '30px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <h3>{expense ? 'Edit Expense' : 'Add New Expense'}</h3>
        
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
            Category *
          </label>
          <select
            value={formData.category_id}
            onChange={(e) => setFormData({...formData, category_id: e.target.value})}
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '5px'
            }}
            required
          >
            <option value="">Select Category</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.icon} {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
            Title *
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({...formData, title: e.target.value})}
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '5px'
            }}
            placeholder="e.g., Electricity Bill, Cleaning Supplies"
            required
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
              Amount (₹) *
            </label>
            <input
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({...formData, amount: e.target.value})}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '5px'
              }}
              placeholder="0.00"
              step="0.01"
              min="0"
              required
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
              Date *
            </label>
            <input
              type="date"
              value={formData.expense_date}
              onChange={(e) => setFormData({...formData, expense_date: e.target.value})}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '5px'
              }}
              required
            />
          </div>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
            Vendor (Optional)
          </label>
          <select
            value={formData.vendor_id}
            onChange={(e) => setFormData({...formData, vendor_id: e.target.value})}
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '5px'
            }}
          >
            <option value="">No Vendor / Direct Purchase</option>
            {vendors.map(vendor => (
              <option key={vendor.id} value={vendor.id}>
                {vendor.name} {vendor.service_categories?.name ? `(${vendor.service_categories.name})` : ''}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
            Payment Method
          </label>
          <select
            value={formData.payment_method}
            onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '5px'
            }}
          >
            <option value="cash">Cash</option>
            <option value="upi">UPI</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="cheque">Cheque</option>
            <option value="card">Card</option>
          </select>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '5px'
            }}
            rows="3"
            placeholder="Add any additional details about this expense..."
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
            Notes
          </label>
          <input
            type="text"
            value={formData.notes}
            onChange={(e) => setFormData({...formData, notes: e.target.value})}
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '5px'
            }}
            placeholder="Any internal notes..."
          />
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={handleSubmit}
            style={{
              flex: 1,
              padding: '12px',
              background: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            {expense ? 'Update' : 'Add'} Expense
          </button>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '12px',
              background: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// Status Badge Component
function StatusBadge({ status }) {
  const colors = {
    pending: '#ffc107',
    approved: '#17a2b8',
    paid: '#28a745',
    rejected: '#dc3545'
  };

  return (
    <span style={{
      padding: '3px 10px',
      borderRadius: '15px',
      background: colors[status] || '#6c757d',
      color: 'white',
      fontSize: '12px',
      fontWeight: '600'
    }}>
      {status?.toUpperCase()}
    </span>
  );
}

// Styles
const selectStyle = {
  padding: '10px',
  border: '2px solid #e0e0e0',
  borderRadius: '8px',
  background: 'white',
  cursor: 'pointer'
};

const tableHeaderStyle = {
  padding: '12px',
  textAlign: 'left',
  borderBottom: '2px solid #dee2e6',
  fontWeight: '600',
  color: '#495057'
};

const tableCellStyle = {
  padding: '12px',
  borderBottom: '1px solid #dee2e6'
};

const actionButtonStyle = (color) => ({
  padding: '5px 10px',
  background: color,
  color: 'white',
  border: 'none',
  borderRadius: '5px',
  cursor: 'pointer',
  fontSize: '12px'
});

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000
};

const modalContentStyle = {
  background: 'white',
  borderRadius: '10px',
  padding: '30px',
  width: '90%',
  maxWidth: '600px',
  maxHeight: '90vh',
  overflow: 'auto'
};

const labelStyle = {
  display: 'block',
  marginBottom: '5px',
  fontWeight: '600',
  color: '#333'
};

const inputStyle = {
  width: '100%',
  padding: '10px',
  border: '1px solid #ddd',
  borderRadius: '5px'
};

const submitButtonStyle = {
  flex: 1,
  padding: '12px',
  background: '#28a745',
  color: 'white',
  border: 'none',
  borderRadius: '5px',
  cursor: 'pointer',
  fontWeight: '600'
};

const cancelButtonStyle = {
  flex: 1,
  padding: '12px',
  background: '#6c757d',
  color: 'white',
  border: 'none',
  borderRadius: '5px',
  cursor: 'pointer',
  fontWeight: '600'
};

export default ExpenseManagement;